// voltage: free shipping goal by region
// Copy of the vendor <free-shipping-goal> keyed on region (US / CA / INTL) instead of currency.

import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

const REGION_KEY = "voltage-fsg-region";
const REGION_TTL = 24 * 60 * 60 * 1000;
const toRegion = (code) => (code === "US" ? "US" : code === "CA" ? "CA" : code ? "INTL" : "US");

let regionPromise = null;
function detectRegion(server) {
  if (server && server !== "auto") return Promise.resolve(server);
  try {
    const cached = JSON.parse(sessionStorage.getItem(REGION_KEY) || "null");
    if (cached?.region && Date.now() - cached.t < REGION_TTL) return Promise.resolve(cached.region);
  } catch (_e) {
    // storage blocked
  }
  if (regionPromise) return regionPromise;
  regionPromise = fetch(`${window.Shopify?.routes?.root || "/"}browsing_context_suggestions.json?country[enabled]=true`, {
    headers: { Accept: "application/json" },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const code = String(data?.detected_values?.country?.handle || "").toUpperCase();
      const region = toRegion(code);
      try {
        sessionStorage.setItem(REGION_KEY, JSON.stringify({ t: Date.now(), region }));
      } catch (_e) {
        // storage blocked
      }
      return region;
    })
    .catch(() => "US");
  return regionPromise;
}

class FreeShippingGoalVoltage extends HTMLElement {
  static get observedAttributes() {
    return ["data-cart-total"];
  }

  #region = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#render();
    detectRegion(this.dataset.region).then((region) => {
      this.#region = region;
      this.dataset.regionResolved = region;
      this.#render();
    });
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  // morph skips our subtree but still syncs attributes
  attributeChangedCallback() {
    if (this.isConnected) this.#render();
  }

  #onCartUpdate = (event) => {
    const sections = event.detail?.data?.sections;
    const sectionId = this.closest("[data-section-id]")?.dataset.sectionId;
    if (!sections || !sectionId || !sections[sectionId]) return;
    const fresh = new DOMParser().parseFromString(sections[sectionId], "text/html").querySelector("free-shipping-goal-voltage");
    if (fresh?.dataset.cartTotal && fresh.dataset.cartTotal !== this.dataset.cartTotal) this.dataset.cartTotal = fresh.dataset.cartTotal;
    this.#render();
  };

  get #goalCents() {
    const region = this.#region || this.dataset.region;
    const key = { US: "goalUs", CA: "goalCa", INTL: "goalIntl" }[region] || "goalUs";
    const amount = parseFloat(this.dataset[key]);
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
  }

  #render() {
    const cartTotal = parseInt(this.dataset.cartTotal, 10);
    const goal = this.#goalCents;
    // no amount set for this region
    this.toggleAttribute("data-goal-empty", goal <= 0);
    if (!Number.isFinite(cartTotal) || cartTotal < 0 || goal <= 0) return;

    const qualified = cartTotal >= goal;
    this.querySelector(".free-shipping-goal__congratulations")?.classList.toggle("hidden", !qualified);
    this.querySelector(".free-shipping-goal__remaining")?.classList.toggle("hidden", qualified);
    if (!qualified) {
      const remaining = this.querySelector(".free-shipping-goal__remaining-amount");
      if (remaining) {
        const moneyFormat = window.FoxTheme?.moneyFormat || window.Shopify?.currency?.money_format;
        remaining.textContent = formatCurrency(goal - cartTotal, moneyFormat);
      }
    }
    this.querySelector("progress-bar")?.setAttribute("percent", String(Math.min((cartTotal / goal) * 100, 100)));
  }
}

if (!customElements.get("free-shipping-goal-voltage")) customElements.define("free-shipping-goal-voltage", FreeShippingGoalVoltage);
